import React from "react"
import { graphql } from "gatsby"
import Layout from "gatsby-theme-blank/src/components/Layout/layout"
import SEO from "gatsby-theme-blank/src/components/seo"
import NonStretchedImage from "gatsby-theme-blank/src/components/Image/non-stretched-image"
import AdditionalInformation from "gatsby-theme-blank/src/components/additional-information"
import {
  pageActu,
  gridAriane,
  gridTitle,
  gridDate,
  gridTags,
  gridContent,
  gridResume,
  pageActuAdditionalInfos,
  actualiteImg,
  actualiteTaxo,
  actualiteTitle,
  actualiteContentFooter,
  actualiteContentFooterBack,
  socialPanelContainer,
} from "./styles/detailsActu.module.scss"
import PropTypes from "prop-types"
import {
  AdditionalInfos,
  ActuTaxo,
  ActuTitle,
  ActualiteFooterContainer,
  GridDate,
} from "./styled-components/actualite-styled"
import TransitionFade from "gatsby-theme-blank/src/components/transition-fade"
import Ariane from "gatsby-theme-blank/src/components/Ariane/ariane"
import SocialPanel from "gatsby-theme-blank/src/components/SocialMedias/social-panel"
import { ContentSplitter } from "gatsby-theme-blank/src/components/ContentSplitter/content-splitter"
import SimpleReactLightbox, { SRLWrapper } from "simple-react-lightbox"
import { useBuildData } from "gatsby-theme-blank/src/hooks/use-build-data"

//-----------------------QueryGraphql---------------------------//

export const query = graphql`
  query($slug: String!) {
    nodeActualites(path: { alias: { eq: $slug } }) {
      ...ActualiteNewThemeItem
    }
  }
`

//-----------------------QueryGraphql---------------------------//

//-----------------------Parent component---------------------------//

const ActualiteTemplate = ({ data, location }) => {
  const nodeActualites = data.nodeActualites
  const allTaxos = nodeActualites.relationships.field_taxonomie_thematique
    .map((taxo) => {
      return taxo.name
    })
    .join()
  return (
    <Layout isDetail={true}>
      <SEO
        title={nodeActualites.title}
        description={
          "Dans cette page, vous découvrez l'actualité suivante : " +
          nodeActualites.title +
          ", mais aussi l'ensemble des thématiques suivantes : " +
          allTaxos
        }
        nodeImageUrl={
          nodeActualites.relationships?.field_image_actus?.localFile
            ?.SlugifiedPublicUrl
        }
      />
      <div className={pageActu}>
        <ActualiteImg nodeActualites={nodeActualites} />
        <div className={gridAriane}>
          <Ariane
            pageNameLink="/actualites"
            pageName="Toutes les actualités"
            isDetail={true}
            postName={nodeActualites.title}
          />
        </div>
        <GridDate className={gridDate}>
          {nodeActualites.created.split("/").join(".")}
        </GridDate>
        <div className={gridTitle}>
          <ActuTitle className={actualiteTitle}>
            {nodeActualites.title}
          </ActuTitle>
        </div>
        <ActualiteTags nodeActualites={nodeActualites} />
        <ActualiteContent nodeActualites={nodeActualites} />
      </div>
      <ActualiteContentFooter
        nodeActualites={nodeActualites}
        location={location.href}
      />
      <ActualiteInfos nodeActualites={nodeActualites} />
    </Layout>
  )
}

//-----------------------Parent component---------------------------//

//-----------------------Child component/Content---------------------------//

const ActualiteContent = ({ nodeActualites }) => {
  return (
    <>
      <ContentSplitter
        node={nodeActualites}
        resumeClassName={gridResume}
        contentClassName={gridContent}
        splitPoint={625}
      />
    </>
  )
}

//-----------------------Child component/Content---------------------------//

//-----------------------Child component/Tags---------------------------//

const ActualiteTags = ({ nodeActualites }) => {
  return (
    <>
      {nodeActualites.relationships.field_taxonomie_thematique && (
        <div className={gridTags}>
          <p>Thématique</p>
          {nodeActualites.relationships.field_taxonomie_thematique.map(
            (taxo, index) => {
              if (
                index ===
                nodeActualites.relationships.field_taxonomie_thematique.length -
                  1
              ) {
                return (
                  <ActuTaxo key={taxo.name} className={actualiteTaxo}>
                    <TransitionFade link={taxo.path.alias}>
                      {taxo.name}
                    </TransitionFade>
                  </ActuTaxo>
                )
              }
              return (
                <ActuTaxo key={taxo.name} className={actualiteTaxo}>
                  <TransitionFade link={taxo.path.alias}>
                    {taxo.name},&nbsp;
                  </TransitionFade>
                </ActuTaxo>
              )
            }
          )}
        </div>
      )}
    </>
  )
}

//-----------------------Child component/Tags---------------------------//

//-----------------------Child component/Img---------------------------//

const ActualiteImg = ({ nodeActualites }) => {
  const themeColors = useBuildData().theme_settings.palette
  const options = {
    buttons: {
      backgroundColor: themeColors.b_color_gradients.b_dark,
      showAutoplayButton: false,
      showCloseButton: true,
      showDownloadButton: false,
      showFullscreenButton: true,
      showNextButton: false,
      showPrevButton: false,
      showThumbnailsButton: false,
    },
  }
  return (
    <div className={actualiteImg}>
      <SimpleReactLightbox>
        <SRLWrapper options={options}>
          {nodeActualites.relationships?.field_image_actus?.localFile && (
            <NonStretchedImage
              alt={nodeActualites.field_image_actus.alt}
              {...nodeActualites.relationships.field_image_actus.localFile}
              ariaHidden={true}
            />
          )}
        </SRLWrapper>
      </SimpleReactLightbox>
    </div>
  )
}

//-----------------------Child component/Img---------------------------//

//-----------------------Child component/ContentFooter---------------------------//

const ActualiteContentFooter = ({ nodeActualites, location }) => (
  <>
    <ActualiteFooterContainer className={actualiteContentFooter}>
      <div className={actualiteContentFooterBack}>
        <TransitionFade arialabel="Retour aux actualités" link="/actualites">
          Retour aux actualités
        </TransitionFade>
      </div>
      <div className={socialPanelContainer}>
        <SocialPanel location={location} postData={nodeActualites} />
      </div>
    </ActualiteFooterContainer>
  </>
)

//-----------------------Child component/ContentFooter---------------------------//

//-----------------------Child component/Infos---------------------------//

const ActualiteInfos = ({ nodeActualites }) => (
  <>
    {nodeActualites.field_infos_complementaires && (
      <AdditionalInfos className={pageActuAdditionalInfos}>
        <AdditionalInformation node={nodeActualites} />
      </AdditionalInfos>
    )}
  </>
)

//-----------------------Child component/Infos---------------------------//

//-----------------------Styled component---------------------------//

//-----------------------Styled component---------------------------//

//-----------------------Prop-Types---------------------------//

ActualiteTemplate.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object,
}
ActualiteContent.propTypes = {
  nodeActualites: PropTypes.object.isRequired,
}
ActualiteImg.propTypes = {
  nodeActualites: PropTypes.object.isRequired,
}
ActualiteTags.propTypes = {
  nodeActualites: PropTypes.object.isRequired,
}
ActualiteContentFooter.propTypes = {
  nodeActualites: PropTypes.object.isRequired,
  location: PropTypes.object,
}
ActualiteInfos.propTypes = {
  nodeActualites: PropTypes.object.isRequired,
}

//-----------------------Prop-Types---------------------------//

export default ActualiteTemplate
